// Roles Store - manages available roles from the database
// Persists to localStorage for demo purposes

import type { UserRole } from '@/contexts/AuthContext'

export interface RoleOption {
  value: UserRole | string
  label: string
  description: string
  isCustom?: boolean
}

// Default system roles (always available)
const DEFAULT_ROLES: RoleOption[] = [
  { value: 'viewer', label: 'Viewer', description: 'View drops and photos only' },
  { value: 'manager', label: 'Manager', description: 'Create and manage drops' },
  { value: 'admin', label: 'Admin', description: 'Full access except billing' },
]

const STORAGE_KEY = 'photodrop_custom_roles'

// Get custom roles from localStorage
function getCustomRoles(): RoleOption[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      return JSON.parse(stored)
    }
  } catch (e) {
    console.error('Failed to load custom roles:', e)
  }
  return []
}

// Save custom roles to localStorage
function saveCustomRoles(roles: RoleOption[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(roles))
  } catch (e) {
    console.error('Failed to save custom roles:', e)
  }
}

// Get all available roles (system + custom)
export function getAvailableRoles(): RoleOption[] {
  const customRoles = getCustomRoles()
  return [...DEFAULT_ROLES, ...customRoles]
}

// Add a custom role
export function addCustomRole(role: Omit<RoleOption, 'isCustom'>): RoleOption {
  const customRoles = getCustomRoles()
  const newRole: RoleOption = {
    ...role,
    isCustom: true,
  }
  customRoles.push(newRole)
  saveCustomRoles(customRoles)
  return newRole
}

// Remove a custom role
export function removeCustomRole(value: string): boolean {
  const customRoles = getCustomRoles()
  const index = customRoles.findIndex((r) => r.value === value)
  if (index === -1) return false

  customRoles.splice(index, 1)
  saveCustomRoles(customRoles)
  return true
}

// Check if a role exists
export function roleExists(value: string): boolean {
  return getAvailableRoles().some((r) => r.value === value)
}

// Reset custom roles (for testing)
export function resetCustomRoles(): void {
  localStorage.removeItem(STORAGE_KEY)
}
